import { useId, useState } from 'react';
import {
  Alert,
  Button,
  FieldSet,
  FormHelp,
  Heading,
  Label,
  LabeledControl,
  Legend,
  Modal,
  Radio,
  VisuallyHidden,
} from '@zillow/constellation-10';
import { LandlordBusinessType } from '@zg-rentals/ts-rent-payment-client';

const FOLLOW_UP_MESSAGE = 'We will reach out for additional information';

type Props = {
  onClose: () => void;
  onConfirm: (businessType: LandlordBusinessType) => void;
  isSubmitting?: boolean;
};

const ConfirmBusinessTypeModal = ({ onClose, onConfirm, isSubmitting = false }: Props): JSX.Element => {
  const [businessType, setBusinessType] = useState<LandlordBusinessType | null>(null);
  const radioGroupName = useId();
  const isCompany = businessType === LandlordBusinessType.COMPANY;

  return (
    <Modal
      open
      onOpenChange={(isOpen) => !isOpen && onClose()}
      size={{ base: 'fullScreen', md: 'md' }}
      header={<Heading level={2}>Confirm how you collect rental income</Heading>}
      body={
        <>
          <FieldSet
            required
            legend={<Legend>How do you collect rental income?</Legend>}
            description={
              <FormHelp>
                To ensure your 1099 tax form shows the right name and tax ID, please confirm whether you collect your
                rental income as an individual or as a business entity
              </FormHelp>
            }
          >
            <LabeledControl
              control={
                <Radio
                  name={radioGroupName}
                  value={LandlordBusinessType.INDIVIDUAL}
                  checked={businessType === LandlordBusinessType.INDIVIDUAL}
                  onChange={() => setBusinessType(LandlordBusinessType.INDIVIDUAL)}
                />
              }
              label={<Label>I collect rent as an individual</Label>}
            />
            <LabeledControl
              control={
                <Radio
                  name={radioGroupName}
                  value={LandlordBusinessType.COMPANY}
                  checked={isCompany}
                  onChange={() => setBusinessType(LandlordBusinessType.COMPANY)}
                />
              }
              label={<Label>I collect rent as a business entity</Label>}
            />
          </FieldSet>

          {isCompany && <Alert css={{ marginTop: 'default' }}>{FOLLOW_UP_MESSAGE}</Alert>}

          {/* the Alert mounts together with its text, so screen readers miss it; this region is always present */}
          <VisuallyHidden>
            <output aria-live="polite" aria-atomic="true">
              {isCompany ? FOLLOW_UP_MESSAGE : ''}
            </output>
          </VisuallyHidden>
        </>
      }
      footer={
        <Button
          emphasis="filled"
          loading={isSubmitting}
          disabled={!businessType}
          onClick={() => businessType && onConfirm(businessType)}
        >
          Submit
        </Button>
      }
    />
  );
};

export default ConfirmBusinessTypeModal;
